/* Atlas v3 — the Cycle page's flat world dot-map ("Atlas watches the world").
   Pure SVG from the committed worldDots.js grid (generated dev-time with
   dotted-map — no runtime dependency, no WebGL). Land is a dim dot field;
   the signal geographies pulse amber — or red when any signal sourced there
   is in a turning state (including a live supply-tripwire BREAK). City
   labels appear only when a pin is tapped (tap again or tap the map to
   dismiss) — no legend, per Ben. Texture with one job; the starfield
   stays on Overview.

   `hotGeos` — Set of GEO_PINS keys whose signals are currently turning. */
import { useState } from 'react'
import { MAP_W, MAP_H, GEO_PINS, WORLD_DOTS } from './worldDots'

/* Hand-placed label anchors so the Asia cluster (Beijing/Seoul/Tokyo/Taipei)
   doesn't collide: dx/dy in viewBox units + SVG text-anchor. */
const LABEL_POS = {
  us: { dx: 1.4, dy: 0.6, anchor: 'start' },
  sf: { dx: 0, dy: 2.7, anchor: 'middle' },
  netherlands: { dx: 0, dy: -1.7, anchor: 'middle' },
  china: { dx: -1.4, dy: 0.6, anchor: 'end' },
  korea: { dx: 0, dy: 2.7, anchor: 'middle' },
  taiwan: { dx: 0, dy: 2.7, anchor: 'middle' },
  japan: { dx: 1.4, dy: 0.6, anchor: 'start' },
}

export function CycleMap({ hotGeos }) {
  const [active, setActive] = useState(null)
  return (
    <svg
      viewBox={`0 0 ${MAP_W} ${MAP_H}`}
      style={{ width: '100%', height: 'auto', display: 'block' }}
      role="img" aria-label="World map of the signal sources — tap a pin for its city"
      onClick={() => setActive(null)}
    >
      {WORLD_DOTS.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r={0.3} fill="hsl(var(--studio-text-subtle) / 0.5)" />
      ))}
      {Object.entries(GEO_PINS).map(([key, p]) => {
        const hot = hotGeos?.has(key)
        const tok = hot ? '--studio-danger' : '--studio-accent'
        const lp = LABEL_POS[key] ?? { dx: 0, dy: 2.7, anchor: 'middle' }
        return (
          <g key={key} style={{ cursor: 'pointer' }}
            onClick={(e) => { e.stopPropagation(); setActive(active === key ? null : key) }}>
            {/* invisible hit target (~30px on a 390px phone) */}
            <circle cx={p.x} cy={p.y} r={3.2} fill="transparent" />
            <circle className="v3-map-ping" cx={p.x} cy={p.y} r={1.5} fill="none"
              stroke={`hsl(var(${tok}))`} strokeWidth={0.22} />
            <circle cx={p.x} cy={p.y} r={0.75} fill={`hsl(var(${tok}))`} />
            {active === key && (
              <text x={p.x + lp.dx} y={p.y + lp.dy} textAnchor={lp.anchor}
                paintOrder="stroke" stroke="hsl(var(--studio-background))" strokeWidth={0.6}
                style={{ fontFamily: 'var(--studio-font-mono)', fontSize: 2, letterSpacing: '0.08em' }}
                fill={hot ? `hsl(var(${tok}))` : 'hsl(var(--studio-text) / 0.95)'}>
                {p.label}
              </text>
            )}
          </g>
        )
      })}
    </svg>
  )
}
